import { Component, Input } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectedItemDetails, CollectedMagicCard, CollectedPreviewItem, GameType, GetMagicCardRequest, GetMagicCardResponse, ItemType, MagicCard, MagicCardFace, MagicSearch, SearchRequest, SearchResponse, ViaSet } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { take } from "rxjs";
import { MagicCardTextComponent } from "./magic-card-text.component";
import { MagicCardVersionsComponent } from "./magic-card-versions.component";
import { Title } from "@angular/platform-browser";
import { ItemCollectionCountComponent } from "@ponder-shared/buttons/item-collection-count.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, intializeBaseSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { environment } from "../../../../environments/environment";
import { canFlip, shortenedName } from "@ponder-shared/utils/general";
import { MagicCardInDecksComponent } from "./magic-card-in-decks.component";
import { MagicCardDetailsComponent } from "./magic-card-details.component";
import { MagicCardAdminComponent } from "./magic-card-admin.component";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component ({
    selector: "magic-card",
    imports: [
        LoadingComponent,
        ItemCollectionCountComponent,
        MagicCardTextComponent,
        MagicCardVersionsComponent,
        MagicCardInDecksComponent,
        MagicCardDetailsComponent,
        MagicCardAdminComponent
    ],
    templateUrl: "./magic-card.component.html"
})
export class MagicCardComponent {
    @Input() public setCode!: string;       // via the route
    @Input() public setIndex!: string;
    @Input() public cardName?: string;

    public username?: string;
    public isAuthed: boolean = false;
    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public readonly flipIconSvg = SvgIconMap.get(SvgIcon.Flip)!;

    public collectedMagicCard?: CollectedMagicCard;
    public magicCard?: MagicCard;
    public collectedDetails?: CollectedItemDetails;

    private frontImageUrl?: string;
    private backImageUrl?: string;
    public cardImageUrl?: string;
    private displayBackface: boolean = false;

    public frontFace?: MagicCardFace;
    public backFace?: MagicCardFace;

    public reprints?: CollectedPreviewItem[];
    public reprintCurrIndex = 0;


    public constructor(
        private ponderDataClient: PonderDataClient, 
        private authService : AuthService,
        private pageTitle: Title,
        private events: EventBroker) {
        console.debug("hi???");
    }

    /**
     * Fetches the card denoted by the set code and index into that set.
     */
    public ngOnInit(): void {
        this.authService.getSessionAsync().then((authSession?: AuthSession) => {
            this.username = authSession?.username;
            this.isAuthed = this.username ? true : false;
            this.fetchCard();
        });
    }

    public ngOnChanges(): void {
        // @Input has changed (page reload/back/forward typically)
        if (this.magicCard) {
            this.reset();
            this.fetchCard();
        }
    }

    private reset(): void {
        this.isLoading = true;
        this.loadingSuccess = false;
        this.collectedMagicCard = undefined;
        this.magicCard = undefined;
        this.collectedDetails = undefined;
    }

    private fetchCard(): void {
        let cardRequest: GetMagicCardRequest = new GetMagicCardRequest({
            preview: false,
            setProps: new ViaSet({
                setCode: this.setCode,
                setIndex: this.setIndex
            })
        });

        this.ponderDataClient.magicCard(cardRequest).pipe(take(1)).subscribe({
            next: (response: GetMagicCardResponse) => {
                if (response.success) {
                    this.loadingSuccess = true;
                    this.collectedMagicCard = response.cards?.at(0);
                    this.magicCard = this.collectedMagicCard?.card;
                    this.collectedDetails = this.collectedMagicCard?.collected;

                    if (this.magicCard) {
                        this.pageTitle.setTitle(`Ponder > ${this.magicCard.name} (${this.setCode!.toUpperCase()})`);

                        this.frontFace = this.magicCard.faces?.at(0);
                        this.backFace = this.magicCard.faces?.at(1);

                        if (this.backFace?.isFront) {
                            let t = this.backFace;
                            this.backFace = this.frontFace;
                            this.frontFace = t;
                        }
                        
                        this.frontImageUrl = `${environment.ponderImageBaseUrl}/card/large/front/${this.magicCard.id}.jpg`;
                        this.backImageUrl = `${environment.ponderImageBaseUrl}/card/large/back/${this.magicCard.id}.jpg`;
                        this.cardImageUrl = this.frontImageUrl;
                    }

                    this.sendNavbarUpdate();
                    this.fetchReprints();
                }
            },
            error: () => {
                this.sendNavbarUpdate();
            }
        })
        .add(() => { this.isLoading = false; });
    }



    /**
     * Updates the navbar / breadcrumbs for the current card and its parent set(s) and/or block.
     */
    private sendNavbarUpdate(): void {
        var segments = intializeBaseSegmentArray(GameType.Magic);

        if (!this.magicCard || !segments) {
            this.events.ui.navbarUpdateEvent.emit();
            return;
        }

        addSegmentToList(segments, this.magicCard.set.block?.name, this.magicCard.set.block?.code?.toUpperCase(), `/magic/block/${this.magicCard.set.block?.code}`);
        addSegmentToList(segments, this.magicCard.set.parentSet?.name, this.magicCard.set.parentSet?.code?.toUpperCase(), `/magic/set/${this.magicCard.set.parentSet?.code}`);
        addSegmentToList(segments, this.magicCard.set.name, this.magicCard.set.code?.toUpperCase(), `/magic/set/${this.magicCard.set.code}`);
        addSegmentToList(segments, this.magicCard.name, shortenedName(this.magicCard.name!, 20));

        this.events.ui.navbarUpdateEvent.emit(segments);
    }

    /**
     * Retrieves and sorts a list of all reprints with the same name.
     */
    private fetchReprints() {
        var searchRequest = new SearchRequest({
            game: GameType.Magic,
            magic: new MagicSearch({
                oracleId: this.magicCard!.oracleId!
            })
        });

        this.ponderDataClient
            .search(searchRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResponse) => {
                    this.reprints = response.results;

                    if (this.reprints) {
                        this.reprintCurrIndex = this.reprints.findIndex(r => (r.item.data!.magic!.setIndex == this.setIndex) && (r.item.data!.magic!.set.code == this.setCode));
                    }
                }
            })
    }

    public canFlip(): boolean {
        return canFlip(this.magicCard!.layout!);
    }

    public flipFace(): void {
        if (this.magicCard && this.magicCard.faces!.length > 1) {
            this.displayBackface = !this.displayBackface;
            this.cardImageUrl = (this.displayBackface ? this.backImageUrl : this.frontImageUrl);
        }
    }

    public getName(forceEnglish: boolean): string | undefined {
        if (this.frontFace?.localizedName && !forceEnglish) {
            return this.frontFace.localizedName;
        }
        return this.frontFace?.name;
    }

    public hasFlavorName(): boolean {
        if (this.frontFace?.flavorName) {
            return true;
        }

        return false;
    }

    public getFlavorName(): string | undefined {
        return this.frontFace?.flavorName;
    }

    public getTypeLine(forceEnglish: boolean): string | undefined {
        if (this.frontFace?.localizedTypeLine && !forceEnglish) {
            return this.frontFace.localizedTypeLine;
        }
        
        return this.frontFace?.typeLine;
    }
    
    public getText(forceEnglish: boolean): string | undefined {
        if (this.frontFace?.localizedText && !forceEnglish) {
            return this.frontFace.localizedText;
        }

        return this.frontFace?.oracleText;
    }

    public getFlavorText(): string | undefined {
        if (this.frontFace?.localizedText) {
            // The localized text also includes the localized flavor text.
            return "";
        }

        return this.frontFace?.flavorText;
    }

    public getArtistName(): string | undefined {
        return this.frontFace?.artist?.name;
    }

    public getSetName(): string | undefined {
        return this.magicCard?.set.name;
    }

    public getGameType(): GameType {
        return GameType.Magic;
    }

    public getItemType(): ItemType {
        return ItemType.Card;
    }

    public isLocalized(): boolean {
        if (this.frontFace?.localizedName) {
            return true;
        }

        return false;
    }

    public getCollectedNormalCount(): number {
        return (this.collectedDetails?.count ? this.collectedDetails!.count : 0);
    }

    public getCollectedSpecialCount(): number {
        return (this.collectedDetails?.specialCount ? this.collectedDetails!.specialCount : 0);
    }

    public getCollectedProxyCount(): number {
        return (this.collectedDetails?.proxyCount ? this.collectedDetails!.proxyCount : 0);
    }
}